 import { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  MenuItem, Stack, Alert, CircularProgress, Divider
} from "@mui/material";
import { stockService } from "../service/CreateStockService";

const SOURCE_OPTIONS   = ["NEW", "EXCHANGE"];
const STATUS_OPTIONS   = ["IN_STOCK", "SOLD", "RETURN"];

/**
 * UpdateStockForm
 *
 * Props:
 *  - bike      {object}    – the bike object to pre-fill the form
 *  - onClose   {function}  – called to close the dialog
 *  - onSuccess {function}  – called after a successful update (to refresh list)
 */
const UpdateStockForm = ({ bike, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    _id:                "",
    date:               "",
    challanNo:          "",
    model:              "",
    skuCode:            "",
    colour:             "",
    chasis:             "",
    engine:             "",
    keyNumber:          "",
    exShowroomPrice:    "",
    purchasePrice:      "",
    location:           "",
    source:             "NEW",
    status:             "IN_STOCK",
    remarks:            "",
    // exchange-only fields
    ownerName:          "",
    registrationNumber: "",
    mobile:             "",
  });

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  // 🔄 Pre-fill when bike prop arrives
  useEffect(() => {
    if (!bike) return;
    setForm({
      _id:                bike._id                || "",
      date:               bike.date
                            ? new Date(bike.date).toISOString().split("T")[0]
                            : "",
      challanNo:          bike.challanNo          || "",
      model:              bike.model              || "",
      skuCode:            bike.skuCode            || "",
      colour:             bike.colour             || "",
      chasis:             bike.chasis             || "",
      engine:             bike.engine             || "",
      keyNumber:          bike.keyNumber          || "",
      exShowroomPrice:    bike.exShowroomPrice     || "",
      purchasePrice:      bike.purchasePrice       || "",
      location:           bike.location           || "",
      source:             bike.source             || "NEW",
      status:             bike.status             || "IN_STOCK",
      remarks:            bike.remarks            || "",
      ownerName:          bike.ownerName          || "",
      registrationNumber: bike.registrationNumber || "",
      mobile:             bike.mobile             || "",
    });
  }, [bike]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUpdate = async () => {
    setError("");
    setSuccess("");

    if (!form._id) {
      setError("Bike ID is missing.");
      return;
    }

    // Build payload — strip exchange fields if source is NEW
    const payload = { ...form };
    if (form.source === "NEW") {
      delete payload.ownerName;
      delete payload.registrationNumber;
      delete payload.mobile;
    }

    setLoading(true);
    try {
      await stockService.updateStock(payload);
      setSuccess("Stock updated successfully!");
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err?.response?.data?.message || "Update failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isExchange = form.source === "EXCHANGE";

  return (
    <Box p={3} sx={{ background: "#f8fafc" }}>
      <Typography variant="h6" fontWeight={700} mb={1}>
        ✏️ Update Stock
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Edit the fields you want to change, then click Save.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>

        {/* ── SECTION: Basic Info ─────────────────────────────────────── */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            BASIC INFO
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Date" name="date"
            type="date" InputLabelProps={{ shrink: true }}
            value={form.date} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Challan No" name="challanNo"
            value={form.challanNo} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Model" name="model"
            value={form.model} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="SKU Code" name="skuCode"
            value={form.skuCode} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Colour" name="colour"
            value={form.colour} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Key Number" name="keyNumber"
            value={form.keyNumber} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Engine No" name="engine"
            value={form.engine} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Chasis No" name="chasis"
            value={form.chasis} onChange={handleChange}
          />
        </Grid>

        {/* ── SECTION: Pricing ────────────────────────────────────────── */}
        <Grid item xs={12} mt={1}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            PRICING
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Purchase Price (₹)" name="purchasePrice"
            type="number" value={form.purchasePrice} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Ex-Showroom Price (₹)" name="exShowroomPrice"
            type="number" value={form.exShowroomPrice} onChange={handleChange}
          />
        </Grid>

        {/* ── SECTION: Classification ─────────────────────────────────── */}
        <Grid item xs={12} mt={1}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            CLASSIFICATION
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth label="Location" name="location"
            value={form.location} onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            select fullWidth label="Source" name="source"
            value={form.source} onChange={handleChange}
          >
            {SOURCE_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            select fullWidth label="Status" name="status"
            value={form.status} onChange={handleChange}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* ── SECTION: Exchange Info (only when source = EXCHANGE) ─────── */}
        {isExchange && (
          <>
            <Grid item xs={12} mt={1}>
              <Typography variant="subtitle2" fontWeight={600}
                sx={{ color: "warning.dark" }}>
                🔁 EXCHANGE VEHICLE INFO
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Owner Name" name="ownerName"
                value={form.ownerName} onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Registration No" name="registrationNumber"
                value={form.registrationNumber} onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Mobile" name="mobile"
                value={form.mobile} onChange={handleChange}
              />
            </Grid>
          </>
        )}

        {/* ── REMARKS ─────────────────────────────────────────────────── */}
        <Grid item xs={12} mt={1}>
          <TextField
            fullWidth multiline rows={2}
            label="Remarks" name="remarks"
            value={form.remarks} onChange={handleChange}
          />
        </Grid>

      </Grid>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdate}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </Stack>
    </Box>
  );
};

export default UpdateStockForm;
